import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { type ChatMentionsMenuItemType } from '@terra/ui-design-system';
import { useMentions } from '../../../hooks/useMentions';
import type { ChatTabType } from '../../../store/chatTabsStore';
import { useChatTabs } from '../../../store/chatTabsStore';
import { useConversationsStore } from '../../../store/conversations-store';
import {
  useTargetScope,
  useOptionalTargetId,
} from '../../../store/projectStore';
import { useTranslations } from 'next-intl';

export function useCreateNewChatTab() {
  const { addChatTab } = useChatTabs();
  const { targetType } = useTargetScope();
  const targetId = useOptionalTargetId();
  const setActiveConversation = useConversationsStore(
    (state) => state.setActiveConversation,
  );
  const { clearMentions } = useMentions();
  const tCopilot = useTranslations('chat.copilot');

  const createNewChatTab = useCallback(
    ({
      contentContainerRef,
      skillDraftId,
      initialDraftInput,
      initialMentions,
      linkedObject,
    }: {
      contentContainerRef?: React.RefObject<HTMLDivElement | null>;
      skillDraftId?: string;
      initialDraftInput?: string;
      initialMentions?: ChatMentionsMenuItemType[];
      linkedObject?: {
        kind: string;
        id: string;
        label?: string;
      };
    }) => {
      const newChatId = uuidv4();
      const newTab: ChatTabType = {
        tabId: newChatId,
        chatId: newChatId,
        conversationId: null,
        title: tCopilot('newChatTitle'),
        targetId,
        targetType,
        mentions: initialMentions ?? [],
        skillDraftId,
        linkedObject,
        draftInput: initialDraftInput,
      };

      addChatTab(newTab);
      clearMentions();
      setActiveConversation(null);

      setTimeout(() => {
        if (contentContainerRef?.current) {
          contentContainerRef.current.scrollLeft =
            contentContainerRef.current.scrollWidth;
        }
      }, 0);
      return newChatId;
    },
    [
      addChatTab,
      clearMentions,
      setActiveConversation,
      targetId,
      targetType,
      tCopilot,
    ],
  );

  return { createNewChatTab };
}
